"use client";

import { createContext, type PropsWithChildren, useContext, useEffect, useState } from "react";
import { useCustomer } from "@/hooks/useCustomer";
import { useWishlistsQuery } from "@/hooks/use-wishlist-queries";
import {
  useCreateWishlistMutation,
  useAddToWishlistMutation,
  useRemoveFromWishlistMutation,
  useDeleteWishlistMutation,
  useRenameWishlistMutation,
  useUpdateWishlistQuantityMutation,
} from "@/hooks/use-wishlist-mutations";
import type { FavListCollection } from "@/lib/wishlist-api";

type WishlistContextType = {
  lists?: FavListCollection[];
  refreshLists: () => void;
  isLoading?: boolean;
  error?: string | null;
  activeListName: string | null;
  activeListId: string | null;
  isEditing: boolean;
  editedName: string;
  setIsEditing: (isEditing: boolean) => void;
  setEditedName: (name: string) => void;
  setActiveList: (listName: string | null) => void;
  createList: (listName: string, productId?: string, dateRange?: { from: string; to: string }) => Promise<void>;
  addToList: (wishlistId: string, productId: string) => Promise<void>;
  removeFromList: (wishlistId: string, productId: string) => Promise<void>;
  deleteList: (wishlistId: string) => Promise<void>;
  renameList: (wishlistId: string, newName: string) => Promise<void>;
  isProductInAnyList: (productId: string) => boolean;
  isProductInList: (listId: string, productId: string) => boolean;
  getListByName: (listName: string) => FavListCollection | undefined;
  getListById: (wishlistId: string) => FavListCollection | undefined;
  getAllLists: () => FavListCollection[];
  isListNameExists: (name: string, excludeId?: string) => boolean;
  updateQuantity: (wishlistId: string, wishlistItemId: string, quantity: number) => void;
  getProductQuantity: (listId: string, productId: string) => number;
};

const WishlistContext = createContext<WishlistContextType | null>(null);

export const WishlistProvider = ({ children }: PropsWithChildren) => {
  const { customer } = useCustomer();
  const customerId = customer?.id || "";

  // Server states
  const { data: lists = [], isLoading, error, refetch } = useWishlistsQuery(customerId);

  // Local states
  const [activeListName, setActiveListName] = useState<string | null>(null);
  const [editedName, setEditedName] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const setActiveList = (listName: string | null, updateEditedName = true) => {
    setActiveListName(listName);
    if (updateEditedName) {
      setEditedName(listName || "");
    }
    setIsEditing(false);
  };

  const activeList = lists?.find((list) => list.listName === activeListName);
  const activeListId = activeList?.id ?? null;

  // Mutations
  const createListMutation = useCreateWishlistMutation();
  const addToListMutation = useAddToWishlistMutation();
  const removeFromListMutation = useRemoveFromWishlistMutation();
  const deleteListMutation = useDeleteWishlistMutation();
  const renameListMutation = useRenameWishlistMutation();
  const { updateQuantity: updateQuantityDebounced } = useUpdateWishlistQuantityMutation();

  useEffect(() => {
    if (lists.length > 0) {
      const activeListExists = activeListName ? lists.some((list) => list.listName === activeListName) : false;

      if (!activeListExists) {
        setActiveList(lists[0].listName, false);
      }
    } else {
      if (activeListName !== null) {
        setActiveList(null, false);
      }
    }
  }, [lists, activeListName]);

  const refreshLists = async () => {
    await refetch();
  };

  const isListNameExists = (name: string, excludeId?: string) => {
    return lists.some((list) => list.listName.toLowerCase() === name.toLowerCase() && list.id !== excludeId);
  };

  const createList = async (listName: string, productId?: string, dateRange?: { from: string; to: string }) => {
    const existingList = lists.find((list) => list.listName.toLowerCase() === listName.toLowerCase());

    if (existingList && productId) {
      await addToList(existingList.id, productId);
      setActiveList(existingList.listName);
      return;
    }

    if (existingList && !productId) return;

    const newList = await createListMutation.mutateAsync({
      listName,
      customerId,
      productId,
      dateRange,
    });

    await refreshLists();

    if (newList?.listName) {
      setActiveList(newList.listName);
    }
  };

  const addToList = async (wishlistId: string, productId: string) => {
    const list = lists.find((l) => l.id === wishlistId);
    if (!list) throw new Error("Wishlist not found");

    if (list.products.some((product) => product.productId === productId)) {
      return;
    }

    await addToListMutation.mutateAsync({ wishlistId, productId });
    await refreshLists();
  };

  const removeFromList = async (wishlistId: string, productId: string) => {
    const list = lists.find((l) => l.id === wishlistId);
    if (!list) throw new Error("Wishlist not found");

    const wishItem = list.products.find((p) => p.productId === productId);
    if (!wishItem?.id) throw new Error("Product not found in wishlist");

    await removeFromListMutation.mutateAsync({
      wishlistItemId: wishItem.id,
      wishlistId,
      productId,
      customerId,
    });
    await refreshLists();
  };

  const deleteList = async (wishlistId: string) => {
    const list = lists.find((l) => l.id === wishlistId);
    if (!list) throw new Error("Wishlist not found");

    const wasActiveList = activeListName === list.listName;

    await deleteListMutation.mutateAsync({ wishlistId, customerId });
    await refreshLists();

    if (wasActiveList) {
      const remainingLists = lists.filter((l) => l.id !== wishlistId);
      setActiveList(remainingLists[0]?.listName || null);
    }
  };

  const renameList = async (wishlistId: string, newName: string) => {
    const list = lists.find((l) => l.id === wishlistId);
    if (!list) throw new Error("Wishlist not found");

    const existingListWithName = lists.find(
      (l) => l.listName.toLowerCase() === newName.toLowerCase() && l.id !== wishlistId
    );
    if (existingListWithName) {
      throw new Error(`A wishlist with the name "${existingListWithName.listName}" already exists`);
    }

    const wasActiveList = activeListName === list.listName;

    await renameListMutation.mutateAsync({ wishlistId, newName, customerId });

    if (wasActiveList) {
      setActiveList(newName);
    }
  };

  const updateQuantity = (wishlistId: string, wishlistItemId: string, quantity: number) => {
    if (quantity < 1) {
      console.error("Quantity must be at least 1");
      return;
    }

    updateQuantityDebounced(wishlistItemId, quantity, wishlistId, customerId);
  };

  const getProductQuantity = (listId: string, productId: string): number => {
    const list = getListById(listId);
    const product = list?.products?.find((p) => p.productId === productId);
    return product?.quantity || 1;
  };

  const isProductInAnyList = (productId: string) => {
    return lists.some((list) => list?.products?.some((product) => product?.productId === productId));
  };

  const isProductInList = (listId: string, productId: string) => {
    const list = getListById(listId);
    return list?.products?.some((product) => product?.productId === productId) || false;
  };

  const getListByName = (listName: string) => {
    return lists.find((list) => list?.listName === listName);
  };

  const getListById = (wishlistId: string) => {
    return lists.find((list) => list?.id === wishlistId);
  };

  const getAllLists = () => {
    return lists || [];
  };

  return (
    <WishlistContext.Provider
      value={{
        lists,
        refreshLists,
        isLoading,
        error: error?.message || null,
        activeListName,
        activeListId,
        isEditing,
        editedName,
        setIsEditing,
        setEditedName,
        setActiveList,
        createList,
        addToList,
        removeFromList,
        deleteList,
        renameList,
        isProductInAnyList,
        isProductInList,
        getListByName,
        getListById,
        getAllLists,
        isListNameExists,
        updateQuantity,
        getProductQuantity,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};
